/* eslint-disable react/require-default-props */
import * as React from 'react';

import type View from 'views/logic/views/View';
import BootstrapModalConfirm from 'components/bootstrap/BootstrapModalConfirm';
import { PaginationProvider, SelectedRowsProvider } from 'common/contexts';
import NewInvestigation from 'security-app/components/Investigations/NewInvestigation';
import type { ModalHandler } from 'security-app/components/Investigations/ModalHandler.type';
import type { InvestigationAPIType, BulkAddEvidenceAPIType } from 'security-app/hooks/api/investigationsAPI.types';
import useEventActionTelemetry from 'components/events/events/hooks/useSendEventActionTelemetry';

import SearchRow from './Search';
import List from './List';

type Props = React.PropsWithChildren<{
  type: 'logs' | 'dashboards' | 'searches' | 'events';
  ids: Array<string>;
  index?: string;
  searchMeta?: {
    searchTitle: string;
    isSearchRelative: boolean;
    saveAsAbsoluteSearch: (newTitle: string) => Promise<View>;
  };
  show?: boolean;
  onClose?: () => void;
  fromBulk?: boolean;
  onSubmit?: (payload: BulkAddEvidenceAPIType) => Promise<void>;
}>;

const modalTitleByType = {
  logs: 'Add log',
  dashboards: 'Add dashboard',
  searches: 'Add search',
  events: 'Add event',
};

const AddEvidenceModal = React.forwardRef(
  (
    {
      type,
      ids,
      index = undefined,
      searchMeta = undefined,
      show = false,
      onClose = undefined,
      fromBulk = false,
      onSubmit = undefined,
      children,
    }: Props,
    ref: React.MutableRefObject<ModalHandler>,
  ) => {
    const newModalRef = React.useRef<ModalHandler>(null);
    const listRef = React.useRef<ModalHandler>(null);
    const [showModal, setShowModal] = React.useState(show);
    const sendEventActionTelemetry = useEventActionTelemetry();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

    const closeModal = () => {
      listRef.current.onCancel();
      setShowModal(false);
      if (onClose) onClose();
    };

    const onConfirm = async () => {
      await listRef.current.onConfirm();

      if (type === 'events') {
        sendEventActionTelemetry('SELECT_AN_INVESTIGATION', fromBulk);
      }
      if (onClose) onClose();
    };

    React.useImperativeHandle(ref, () => ({
      toggle: () => setShowModal(true),
    }));

    return (
      <SelectedRowsProvider<InvestigationAPIType>>
        <BootstrapModalConfirm
          size="lg"
          showModal={showModal}
          onCancel={closeModal}
          onConfirm={onConfirm}
          isAsyncSubmit
          isSubmitting={isSubmitting}
          submitLoadingText="Adding to investigation..."
          title={`Investigations - ${modalTitleByType[type]} as evidence`}>
          {children}
          <PaginationProvider>
            <SearchRow onNewInvestigation={() => setShowModal(false)} newModalRef={newModalRef} />
            <List
              type={type}
              ids={ids}
              index={index}
              setShowModal={setShowModal}
              setIsSubmitting={setIsSubmitting}
              onSubmit={onSubmit}
              searchMeta={searchMeta}
              ref={listRef}
            />
          </PaginationProvider>
        </BootstrapModalConfirm>
        <NewInvestigation ref={newModalRef} onClose={() => setShowModal(true)} />
      </SelectedRowsProvider>
    );
  },
);

export default AddEvidenceModal;
